import { css } from "@emotion/react";
import React from "react";
import { PartialColorProps, useTheme } from "../theme";

export type ProgressBarLabel = (
  props: Readonly<{
    value: number;
    max: number;
    rate: number;
    percent: number;
    children?: never;
  }>,
) => React.JSX.Element;

export interface ProgressBarProps
  extends
    Omit<React.HTMLAttributes<HTMLDivElement>, "color">,
    PartialColorProps {
  /**
   * 進捗度合いを表します。0以上の自然数のみ有効です。
   * また内部的にはこの値を最大値+1で割った剰余が用いられます。
   *
   * @default 0
   */
  value?: number;
  /**
   * 進捗度の最大値を表します。0以上の自然数のみ有効です。
   *
   * @default 0
   */
  max?: number;
  /**
   * ラベルを付与します。
   */
  label?: ProgressBarLabel;
  children?: React.ReactNode;
}

export const ProgressBar = ({
  children,
  color = "default",
  value = 0,
  max = 0,
  className,
  label,
  ...props
}: ProgressBarProps) => {
  const theme = useTheme();
  const rate = value / max;
  const percent = rate * 100;
  return (
    <div {...props}>
      <div
        className={className}
        css={css`
          height: 12px;
          background-color: ${color === "default"
            ? theme.palette.gray[300]
            : theme.palette.intentions[color].light};
          border-radius: 6px;
        `}
      >
        <div
          css={css`
            width: ${percent}%;
            height: 12px;
            background-color: ${color === "default"
              ? theme.palette.gray[500]
              : theme.palette.intentions[color].main};
            border-radius: 6px;
            transition: ${theme.transitions.duration.shorter}
              ${theme.transitions.easing.easeInOut};
          `}
        />
      </div>
      {label ? label({ value, max, rate, percent }) : children}
    </div>
  );
};
